import { useState, useCallback, useRef, useEffect } from 'react'
import { api } from '../lib/api'

interface DataPoint {
  date: string
  value: number
  invested: number
  shares: number
}

const PRESETS = [
  { ticker: 'SPY',  label: 'SPY',  color: '#3ddc84' },
  { ticker: 'NVDA', label: 'NVDA', color: '#a78bfa' },
  { ticker: 'AAPL', label: 'AAPL', color: '#60a5fa' },
  { ticker: 'TSLA', label: 'TSLA', color: '#f87171' },
  { ticker: 'BTC',  label: 'BTC',  color: '#fbbf24' },
  { ticker: 'MSFT', label: 'MSFT', color: '#34d399' },
]

const IPO_YEARS: Record<string, number> = {
  AAPL: 1980, MSFT: 1986, AMZN: 1997, GOOGL: 2004, GOOG: 2004,
  META: 2012, NFLX: 2002, TSLA: 2010, NVDA: 1999, AMD: 1979,
  INTC: 1971, ORCL: 1986, CRM: 2004, UBER: 2019, LYFT: 2019,
  SNAP: 2017, PINS: 2019, RBLX: 2021, COIN: 2021, HOOD: 2021,
  PLTR: 2020, ABNB: 2020, DASH: 2020, RIVN: 2021, LCID: 2021,
  V: 2008, MA: 2006, PYPL: 2015, SQ: 2015, AFRM: 2021,
  SPY: 1993, QQQ: 1999, IWM: 2000, GLD: 2004, TLT: 2002,
  ARKK: 2014, SOXL: 2010, TQQQ: 2010, SQQQ: 2010,
  BTC: 2010, ETH: 2015,
  MRNA: 2018, BNTX: 2019,
  SHOP: 2015, SPOT: 2018, TWLO: 2016, ZM: 2019,
  DDOG: 2019, SNOW: 2020, PATH: 2021, U: 2020,
  SMCI: 2007, ARM: 2023, RDDT: 2024,
}

const CURRENT_YEAR = new Date().getFullYear()

function resolveStartYear(ticker: string, requestedYears: number): { startYear: number; actualYears: number; clipped: number } {
  const ipoYear = IPO_YEARS[ticker.toUpperCase()] ?? null
  const wantedStart = CURRENT_YEAR - requestedYears
  if (ipoYear && ipoYear > wantedStart) {
    const actualYears = CURRENT_YEAR - ipoYear
    return { startYear: ipoYear, actualYears: Math.max(actualYears, 1), clipped: ipoYear - wantedStart }
  }
  return { startYear: wantedStart, actualYears: requestedYears, clipped: 0 }
}

// DCA over REAL dividend-adjusted monthly closes (total return). Each month buys
// $monthly worth of shares at that month's actual price. Because the prices are
// dividend-adjusted, reinvested dividends are already baked into `value`.
function simulateReal(series: { t: number; c: number }[], monthly: number): DataPoint[] {
  const points: DataPoint[] = []
  let totalShares = 0, invested = 0
  for (const pt of series) {
    const price = pt.c
    if (!(price > 0)) continue
    totalShares += monthly / price
    invested    += monthly
    const d = new Date(pt.t * 1000)
    points.push({
      date:    `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`,
      value:   totalShares * price,
      invested,
      shares:  totalShares,
    })
  }
  return points
}

export default function DCA() {
  const timerRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    return () => { if (timerRef.current) clearTimeout(timerRef.current) }
  }, [])

  const [ticker,        setTicker]        = useState('SPY')
  const [monthly,       setMonthly]       = useState(100)
  const [years,         setYears]         = useState(10)
  const [result,        setResult]        = useState<DataPoint[] | null>(null)
  const [loading,       setLoading]       = useState(false)
  const [clippedYears,  setClippedYears]  = useState(0)
  const [actualYears,   setActualYears]   = useState(0)

  const tickerUpper = ticker.toUpperCase()

  const [loadError, setLoadError] = useState('')
  const calculate = useCallback(async () => {
    setLoading(true); setResult(null); setLoadError('')
    try {
      const { series } = await api.market.dcaHistory(ticker.toUpperCase(), years)
      const ay = Math.round(series.length / 12)
      setActualYears(ay)
      setClippedYears(Math.max(0, years - ay))
      setResult(simulateReal(series, monthly))
    } catch (e: any) {
      setLoadError(e.message || 'No historical data for that ticker')
    } finally {
      setLoading(false)
    }
  }, [ticker, monthly, years])

  const final         = result?.[result.length - 1]
  const totalInv      = monthly * (actualYears || years) * 12
  const gain          = final ? final.value - totalInv : 0
  const gainPct       = final ? ((final.value / totalInv - 1) * 100) : 0
  const maxVal        = result ? Math.max(...result.map(r => r.value)) : 1

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div>
        <h1 style={{ fontSize: '1.6rem', color: 'var(--bill)', letterSpacing: '-0.02em', marginBottom: 4 }}>Time Capsule</h1>
        <div className="eyebrow">What if I had invested $X/month into any ticker?</div>
      </div>

      {/* Controls */}
      <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: '20px 24px' }}>
        {/* Row 1: inputs */}
        <div className="dca-controls" style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr', gap: 16, marginBottom: 16 }}>

          {/* Ticker */}
          <div>
            <div className="eyebrow" style={{ marginBottom: 8 }}>Ticker</div>
            <input
              value={ticker}
              onChange={e => { setTicker(e.target.value.toUpperCase()); setResult(null) }}
              placeholder="SPY"
              maxLength={10}
              style={{ background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '10px 14px', color: 'var(--text)', fontFamily: 'Syne Mono', fontSize: '1rem', outline: 'none', width: '100%', letterSpacing: '.05em' }}
            />
            <div style={{ display: 'flex', gap: 5, marginTop: 7, flexWrap: 'nowrap', overflow: 'hidden' }}>
              {PRESETS.map(p => (
                <button key={p.ticker} onClick={() => { setTicker(p.ticker); setResult(null) }} style={{
                  padding: '3px 7px', background: ticker === p.ticker ? 'var(--stamp)' : 'transparent',
                  border: `1px solid ${ticker === p.ticker ? 'var(--cash-dim)' : 'var(--edge)'}`,
                  color: ticker === p.ticker ? 'var(--cash)' : 'var(--muted)',
                  fontFamily: 'Syne Mono', fontSize: '.6rem', borderRadius: 2, cursor: 'pointer',
                }}>{p.label}</button>
              ))}
            </div>
          </div>

          {/* Monthly */}
          <div>
            <div className="eyebrow" style={{ marginBottom: 8 }}>Monthly ($)</div>
            <input
              type="number" min={1} max={100000}
              value={monthly}
              onChange={e => { setMonthly(Number(e.target.value)); setResult(null) }}
              style={{ background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '10px 14px', color: 'var(--text)', fontFamily: 'Syne Mono', fontSize: '1rem', outline: 'none', width: '100%' }}
            />
            <div style={{ display: 'flex', gap: 5, marginTop: 7 }}>
              {[50, 100, 500, 1000].map(v => (
                <button key={v} onClick={() => { setMonthly(v); setResult(null) }} style={{
                  padding: '3px 8px', background: monthly === v ? 'var(--stamp)' : 'transparent',
                  border: `1px solid ${monthly === v ? 'var(--cash-dim)' : 'var(--edge)'}`,
                  color: monthly === v ? 'var(--cash)' : 'var(--muted)',
                  fontFamily: 'Syne Mono', fontSize: '.6rem', borderRadius: 2, cursor: 'pointer',
                }}>${v}</button>
              ))}
            </div>
          </div>

          {/* Years */}
          <div>
            <div className="eyebrow" style={{ marginBottom: 8 }}>Duration</div>
            <input
              type="number" min={1} max={50}
              value={years}
              onChange={e => { setYears(Number(e.target.value)); setResult(null) }}
              style={{ background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '10px 14px', color: 'var(--text)', fontFamily: 'Syne Mono', fontSize: '1rem', outline: 'none', width: '100%' }}
            />
            <div style={{ display: 'flex', gap: 5, marginTop: 7 }}>
              {[1, 3, 5, 10, 20].map(v => (
                <button key={v} onClick={() => { setYears(v); setResult(null) }} style={{
                  padding: '3px 8px', background: years === v ? 'var(--stamp)' : 'transparent',
                  border: `1px solid ${years === v ? 'var(--cash-dim)' : 'var(--edge)'}`,
                  color: years === v ? 'var(--cash)' : 'var(--muted)',
                  fontFamily: 'Syne Mono', fontSize: '.6rem', borderRadius: 2, cursor: 'pointer',
                }}>{v}yr</button>
              ))}
            </div>
          </div>
        </div>

        {/* Row 2: calculate button full width */}
        <button onClick={calculate} disabled={loading} style={{
          width: '100%', padding: '11px', background: 'var(--cash)', color: 'var(--ink)', border: 'none',
          borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '.82rem',
          letterSpacing: '.06em', textTransform: 'uppercase', cursor: 'pointer',
          opacity: loading ? .6 : 1,
        }}>{loading ? 'Calculating...' : `Calculate ${ticker.toUpperCase()} →`}</button>
        {loadError && (
          <div style={{ marginTop: 10, fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--red)' }}>{loadError}</div>
        )}
      </div>

      {/* Results */}
      {result && final && (
        <>
          {/* Stats row */}
          <div className="stats-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
            {[
              { label: 'Final Value',    val: `$${Math.round(final.value).toLocaleString()}`,  color: 'var(--cash)' },
              { label: 'Total Invested', val: `$${Math.round(totalInv).toLocaleString()}`,     color: 'var(--text)' },
              { label: 'Total Gain',     val: `$${Math.round(gain).toLocaleString()}`,         color: gain >= 0 ? 'var(--cash)' : 'var(--red)' },
              { label: 'Return',         val: `${gainPct >= 0 ? '+' : ''}${gainPct.toFixed(1)}%`, color: gainPct >= 0 ? 'var(--cash)' : 'var(--red)' },
            ].map(s => (
              <div key={s.label} style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: '16px 18px' }}>
                <div className="eyebrow" style={{ marginBottom: 6 }}>{s.label}</div>
                <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1.4rem', color: s.color }}>{s.val}</div>
              </div>
            ))}
          </div>

          {/* Total-return note */}
          <div style={{
            background: 'rgba(133,196,124,.06)', border: '1px solid rgba(133,196,124,.25)',
            borderRadius: 3, padding: '10px 14px', fontFamily: 'Syne Mono', fontSize: '.6rem',
            color: 'var(--cash)', lineHeight: 1.6,
          }}>
            ✦ Total return — figures use dividend-adjusted historical prices, so reinvested dividends are already included in the portfolio value.
          </div>

          {/* IPO clip notice */}
          {clippedYears > 0 && (
            <div style={{ background: 'rgba(212,168,67,.06)', border: '1px solid rgba(212,168,67,.25)', borderRadius: 3, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <img src="/icons/warning.svg" alt="" style={{ width: 16, height: 16, flexShrink: 0 }} />
              <div style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--amber)', lineHeight: 1.6 }}>
                <strong>{tickerUpper} didn't exist {years} years ago</strong> — it {IPO_YEARS[tickerUpper] ? `IPO'd / launched in ${IPO_YEARS[tickerUpper]}` : 'has a shorter history'}. Showing {actualYears} year{actualYears !== 1 ? 's' : ''} instead ({clippedYears} year{clippedYears !== 1 ? 's' : ''} clipped).
              </div>
            </div>
          )}

          {/* Chart */}
          <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: '20px 24px' }}>
            <div className="eyebrow" style={{ marginBottom: 16 }}>
              ${monthly}/mo into {tickerUpper} · {result[0]?.date ?? ''} → {result[result.length - 1]?.date ?? ''}
            </div>
            <div style={{ position: 'relative', height: 240 }}>
              {/* Y axis */}
              <div style={{ position: 'absolute', left: 0, top: 0, bottom: 24, width: 60, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'flex-end', paddingRight: 8 }}>
                {[1, 0.75, 0.5, 0.25, 0].map(p => (
                  <div key={p} style={{ fontFamily: 'Syne Mono', fontSize: '.52rem', color: 'var(--muted)' }}>
                    ${Math.round(maxVal * p / 1000) >= 1 ? `${Math.round(maxVal * p / 1000)}k` : Math.round(maxVal * p)}
                  </div>
                ))}
              </div>

              {/* Chart area */}
              <div style={{ position: 'absolute', left: 64, right: 0, top: 0, bottom: 24 }}>
                <svg width="100%" height="100%" viewBox="0 0 1000 100" preserveAspectRatio="none">
                  {[0, 25, 50, 75, 100].map(y => (
                    <line key={y} x1="0" y1={y} x2="1000" y2={y} stroke="var(--edge)" strokeWidth="0.15" />
                  ))}
                  {/* Invested line */}
                  <polyline
                    points={result.map((p, i) => `${(i / (result.length - 1)) * 1000},${100 - (p.invested / maxVal) * 100}`).join(' ')}
                    fill="none" stroke="rgba(133,196,124,0.3)" strokeWidth="0.6" strokeDasharray="3,3"
                  />
                  {/* Value area fill */}
                  <polygon
                    points={`0,100 ${result.map((p, i) => `${(i / (result.length - 1)) * 1000},${100 - (p.value / maxVal) * 100}`).join(' ')} 1000,100`}
                    fill="rgba(133,196,124,0.04)"
                  />
                  {/* Value line */}
                  <polyline
                    points={result.map((p, i) => `${(i / (result.length - 1)) * 1000},${100 - (p.value / maxVal) * 100}`).join(' ')}
                    fill="none" stroke="var(--cash)" strokeWidth="1.2" vectorEffect="non-scaling-stroke"
                  />
                </svg>

                {/* X axis */}
                <div style={{ display: 'flex', justifyContent: 'space-between', position: 'absolute', bottom: -20, left: 0, right: 0 }}>
                  {result.filter((_, i) => i === 0 || i === result.length - 1 || i % Math.max(1, Math.floor(result.length / 4)) === 0).map((p, i) => (
                    <div key={i} style={{ fontFamily: 'Syne Mono', fontSize: '.52rem', color: 'var(--muted)' }}>{p.date}</div>
                  ))}
                </div>
              </div>
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', gap: 18, marginTop: 28 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--muted)' }}>
                <div style={{ width: 20, height: 2, background: 'var(--cash)' }} /> Portfolio value
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--muted)' }}>
                <div style={{ width: 20, height: 1, borderTop: '1px dashed rgba(133,196,124,0.5)' }} /> Amount invested
              </div>
            </div>
          </div>

          {/* Breakdown table */}
          <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--edge)' }}>
              <span className="eyebrow">Year-by-Year Breakdown</span>
            </div>
            <div className="table-scroll" style={{ overflow: 'auto', maxHeight: 280 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Syne Mono', fontSize: '.68rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--edge)' }}>
                    {['Year', 'Invested', 'Portfolio Value', 'Gain', 'Return'].map(h => (
                      <th key={h} style={{ padding: '8px 14px', textAlign: 'left', color: 'var(--muted)', fontWeight: 400, letterSpacing: '.12em', fontSize: '.58rem' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.filter(p => p.date.length === 4 || p.date.endsWith('-12') || result.indexOf(p) === result.length - 1).map((p, i) => {
                    const g  = p.value - p.invested
                    const gp = ((p.value / p.invested - 1) * 100)
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid var(--edge)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.01)' }}>
                        <td style={{ padding: '8px 14px', color: 'var(--muted)' }}>{p.date}</td>
                        <td style={{ padding: '8px 14px' }}>${Math.round(p.invested).toLocaleString()}</td>
                        <td style={{ padding: '8px 14px', color: 'var(--cash)' }}>${Math.round(p.value).toLocaleString()}</td>
                        <td style={{ padding: '8px 14px', color: g >= 0 ? 'var(--cash)' : 'var(--red)' }}>{g >= 0 ? '+' : ''}${Math.round(g).toLocaleString()}</td>
                        <td style={{ padding: '8px 14px', color: gp >= 0 ? 'var(--cash)' : 'var(--red)' }}>{gp >= 0 ? '+' : ''}{gp.toFixed(1)}%</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ fontFamily: 'Syne Mono', fontSize: '.56rem', color: 'var(--muted)', opacity: .5, textAlign: 'center', lineHeight: 1.6 }}>
            Based on real dividend-adjusted historical prices (total return). Past performance does not guarantee future results. Not financial advice.
          </div>
        </>
      )}
    </div>
  )
}
